import { User, AnonymousUser, StripeSubscription, UserTags, BBOrg } from 'types';
import firebase from '@modules/firebase';
import { clearSignUpType } from './auth-helpers';
import { clearAllAssetsCaches } from './assets-cache';
import { analytics } from '@modules/analytics';
import { apolloClient } from '@services/apollo';
import { UserContextValues } from '@context/user-context';
import { deleteCookie } from 'cookies-next';

type Plan = {
  name: string;
  maxPersonalCards: number;
  canManageSubscription: boolean;
  isBlinqEmployee: boolean;
  isAdmin?: boolean;
} & (
  | {
      type: 'free';
    }
  | {
      type: 'premium';
    }
  | {
      type: 'team';
      isAdmin: boolean;
      maxTeamCards: number;
    }
);

export type TeamPlan = Plan & {
  type: 'team';
  isAdmin: boolean;
  maxTeamCards: number;
};

export function getUserMaxTeamCards(org: BBOrg | undefined): number {
  if (org?.isDemoAccount) return 0;
  if (org?.overrideMaxCards) return org?.overrideMaxCards;
  if (org?.maxCards) return org?.maxCards;
  return Infinity;
}

export function getUserMaxPersonalCards(userTags: UserTags | undefined): number {
  if (userTags?.canCreateMultipleCards) return 5;
  else return 2;
}

export function getUserPlanType(user: User | undefined, userTags: UserTags | undefined): Plan['type'] {
  // if the user doesn't have user tags, we assume their billing never succeeded.
  if (!user || !userTags) return 'free';
  if (user.bbOrgId) return 'team';
  if (userTags?.isPremiumSubscriber) return 'premium';
  else return 'free';
}

export function getUserPlanTypes(user: User | undefined, userTags: UserTags | undefined): Plan['type'][] {
  const planTypes: Plan['type'][] = [];

  if (!user) return ['free'];

  if (user.bbOrgId) planTypes.push('team');

  if (userTags?.isPremiumSubscriber) {
    planTypes.push('premium');
  } else {
    planTypes.push('free');
  }

  return planTypes;
}

export function getActiveSubscription(
  userSubscriptions: StripeSubscription[]
): StripeSubscription | undefined {
  const activeSubscription = userSubscriptions.find(
    ({ status }) => !['canceled', 'unpaid', 'incomplete_expired'].includes(status)
  );

  return activeSubscription;
}

export function getUserPlanName(
  user: User | undefined,
  userTags: UserTags | undefined,
  userSubscriptions: StripeSubscription[]
): string {
  if (!user) return 'Free plan';

  const activeSubscription = getActiveSubscription(userSubscriptions);

  if (user.bbOrgId) {
    if (activeSubscription?.status === 'trialing') {
      return 'Team plan (Trial)';
    }
    if (
      activeSubscription?.status &&
      ['active', 'past_due', 'incomplete'].includes(activeSubscription?.status)
    ) {
      return 'Team plan';
    }
  } else if (userTags?.isPremiumSubscriber) {
    return 'Premium plan';
  }

  return 'Free plan';
}

export function getUserPlanNameFromPlanType(
  planType: Plan['type'],
  user: User | undefined,
  userSubscriptions: StripeSubscription[]
): string {
  const activeSubscription = getActiveSubscription(userSubscriptions);

  switch (planType) {
    case 'free':
      return 'Free plan';
    case 'premium':
      return 'Premium plan';
    case 'team':
      if (user?.bbOrgId) {
        if (activeSubscription?.status === 'trialing') {
          return 'Team plan (Trial)';
        }
        if (
          activeSubscription?.status &&
          ['active', 'past_due', 'incomplete'].includes(activeSubscription?.status)
        ) {
          return 'Team plan';
        }
      } else {
        return 'Free plan';
      }
      break;
    default:
      return '';
  }

  return '';
}

interface UserDetails {
  user: User | undefined;
  userTags: UserTags | undefined;
  userSubscriptions: StripeSubscription[];
  org?: BBOrg | undefined;
}

export function getUserPlans({ user, userTags, userSubscriptions, org }: UserDetails): Plan[] {
  if (!user) {
    return [
      {
        name: getUserPlanName(user, userTags, userSubscriptions),
        type: 'free',
        maxPersonalCards: 2,
        canManageSubscription: false,
        isBlinqEmployee: false,
      },
    ];
  }

  const planTypes = getUserPlanTypes(user, userTags);

  const baseDetails = {
    tags: userTags,
    maxPersonalCards: getUserMaxPersonalCards(userTags),
    canManageSubscription: userSubscriptions.length > 0,
    isBlinqEmployee: userTags?.isBlinqEmployee ?? false,
  };

  const plans: Plan[] = planTypes.map((planType) => {
    if (planType === 'team') {
      return {
        ...baseDetails,
        name: getUserPlanNameFromPlanType(planType, user, userSubscriptions),
        type: 'team',
        isAdmin: !!user.bbOrgId,
        maxTeamCards: getUserMaxTeamCards(org),
      };
    } else {
      return {
        ...baseDetails,
        name: getUserPlanNameFromPlanType(planType, user, userSubscriptions),
        type: planType,
      };
    }
  });

  return plans;
}

export function createAnonymousUser(user: firebase.User, details: Partial<User>): AnonymousUser {
  return {
    userId: user.uid,
    cardIds: details.cardIds ?? [],
    avatarUrl: details.avatarUrl,
    creationTimestampMs: new Date().getTime(),
    firstName: details.firstName,
    lastName: details.lastName,
    isAnonymous: details.isAnonymous ?? true,
    providerId: user.isAnonymous ? 'anonymous' : user.providerId, // user.providerId is always 'firebase'
    companySize: details.companySize,
    country: details.country,
    uiEvents: {
      showBusinessOnboarding: false,
      appDownloaded: false,
    },
  };
}

/**
 * Creates a complete user account (not anonymous). This should be used after the user has signed up via Firebase. This
 * should not be used if you are upgrading a user from an anonymous account to a complete account.
 * @param firebaseUser The newly minted user from Firebase auth
 * @param details Any extra details you want to add to your new user
 */
export function createNewUser(firebaseUser: firebase.User, details: Partial<User>): User {
  return {
    userId: firebaseUser.uid,
    signInEmailAddress: firebaseUser.email ?? undefined,
    cardIds: details.cardIds ?? [],
    avatarUrl: details.avatarUrl,
    creationTimestampMs: new Date().getTime(),
    firstName: details.firstName,
    lastName: details.lastName,
    isAnonymous: false,
    companySize: details.companySize,
    country: details.country,
  };
}

export async function logout(): Promise<void> {
  clearSignUpType();
  await firebase.auth().signOut();

  clearUserDetailsFromCache();
  clearAllAssetsCaches();

  analytics.reset();

  deleteCookie('onboarding-card');

  apolloClient.clearStore();
}

export function clearUserDetailsFromCache(): void {
  localStorage.removeItem('user');
  localStorage.removeItem('userTags');
  localStorage.removeItem('userSubscriptions');
  localStorage.removeItem('bbOrg');

  // We no longer use this but we should keep removing it for the next few months to make sure
  // no one has this in their local storage. - 19/11/2021
  localStorage.removeItem('userData');
}

export function getWorkspaceIdFromOwnerRole(user: UserContextValues | undefined): string {
  const profiles = user?.actor?.profiles ?? [];
  const ownedWorkspaceId = profiles.find((w) => w.role === 'OWNER')?.workspace_id;
  return ownedWorkspaceId ?? '';
}

export function getDefaultWorkspaceIdFromWorkspaceType(user: UserContextValues | undefined): string {
  const profiles = user?.actor?.profiles ?? [];
  const ownerWorkspaceIds = profiles.filter((w) => w.role === 'OWNER');

  const workspaces = user?.actor?.workspaces ?? [];

  // if there is only one workspace where the user is an owner, return that workspace id as it follows it's an indivudual workspace
  let workspaceId = ownerWorkspaceIds[0]?.workspace_id;

  ownerWorkspaceIds.forEach((w) => {
    workspaces.forEach((workspace) => {
      if (workspace.id === w.workspace_id && workspace.type === 'TEAM') {
        // If the user has multiple workspaces, we want to get the id on the team workspace
        workspaceId = workspace.id;
        return;
      }
    });
  });

  return workspaceId;
}

export function isMemberCardCreateEnabled(user: UserContextValues | undefined): boolean {
  const workspaces = user?.actor?.workspaces ?? [];
  const isDisabled = workspaces.some((w) => w.tags?.disable_member_card_creation);
  return !isDisabled;
}

export function isMemberEmailSignatureCreateEnabled(user: UserContextValues | undefined): boolean {
  const workspaces = user?.actor?.workspaces ?? [];
  const isDisabled = workspaces.some((w) => w.tags?.disable_member_email_signature_creation);
  return !isDisabled;
}

export function isMemberVirtualBackgroundCreateEnabled(user: UserContextValues | undefined): boolean {
  const workspaces = user?.actor?.workspaces ?? [];
  const isDisabled = workspaces.some((w) => w.tags?.disable_member_virtual_background_creation);
  return !isDisabled;
}

export function isMemberAccessoryCreateEnabled(user: UserContextValues | undefined): boolean {
  const workspaces = user?.actor?.workspaces ?? [];
  const isDisabled = workspaces.some((w) => w.tags?.disable_member_accessory_creation);
  return !isDisabled;
}

export function isWorkspaceEmailSignaturesEnabled(user: UserContextValues | undefined): boolean {
  const workspaces = user?.actor?.workspaces ?? [];
  const isDisabled = workspaces.some((w) => w.tags?.disable_email_signatures);
  return !isDisabled;
}
